<template>
    <form @submit.prevent="submitForm">
        <div v-if="error" class="error-message-gcn">{{ error }}</div>
        <div v-if="loadingTickerData" class="loading-indicator-gcn small">Cargando datos del ticker...</div>

        <h4 class="section-title">Información Básica <span class="required-indicator">(campos obligatorios *)</span></h4>

        <!-- Ticker Autocomplete -->
        <div class="form-group-gcn">
            <label for="wp-ticker">Ticker <span class="required">*</span></label>
            <input
                type="text"
                id="wp-ticker"
                class="form-control-gcn"
                :class="{ 'error': shouldShowError('ticker') }"
                v-model.trim="tickerSearch"
                @input="debounceFetchAutocomplete"
                @blur="markFieldTouched('ticker'); validateTicker()"
                @focus="markFieldTouched('ticker')"
                placeholder="Buscar ticker (e.g., AAPL)"
                required
                :disabled="isSubmitting"
                autocomplete="off"
            />
            <ul v-if="showSuggestions && tickerSuggestions.length > 0" class="autocomplete-suggestions">
                <li v-for="suggestion in tickerSuggestions" :key="suggestion.ticker" @mousedown.prevent="selectTicker(suggestion.ticker)">
                    {{ suggestion.ticker }} - {{ suggestion.name }}
                </li>
            </ul>
            <input type="hidden" v-model="formData.ticker" />
            <small v-if="shouldShowError('ticker')" class="error-message-gcn">{{ tickerError }}</small>
            <small v-else-if="formData.ticker" class="text-secondary">Ticker seleccionado: {{ formData.ticker }}</small>
        </div>

        <!-- Direction -->
        <div class="form-group-gcn">
            <label>Dirección <span class="required">*</span></label>
            <div>
                <label class="radio-label text-secondary">
                    <input type="radio" v-model="formData.direction" value="1" :disabled="isSubmitting || loadingTickerData" @change="handleTickerInfoChange"> Alcista (Largo)
                </label>
                <label class="radio-label text-secondary">
                    <input type="radio" v-model="formData.direction" value="0" :disabled="isSubmitting || loadingTickerData" @change="handleTickerInfoChange"> Bajista (Corto)
                </label>
            </div>
        </div>

        <!-- Entry Type -->
        <div class="form-group-gcn">
            <label for="wp-entry-type">Tipo de Entrada <span class="required">*</span></label>
            <select 
                id="wp-entry-type" 
                class="form-control-gcn" 
                :class="{ 'error': shouldShowError('entryType') }"
                v-model="formData.entry_type" 
                @change="markFieldTouched('entryType'); validateEntryType()"
                @focus="markFieldTouched('entryType')"
                required 
                :disabled="!tickerInfo.entries || isSubmitting || loadingTickerData">
                <option v-if="!tickerInfo.entries" value="" disabled>Selecciona Ticker y Dirección</option>
                <option v-for="entry in availableEntryTypes" :key="entry.value" :value="entry.value">
                    {{ entry.name }}
                </option>
            </select>
            <small v-if="shouldShowError('entryType')" class="error-message-gcn">{{ entryTypeError }}</small>
        </div>

        <hr class="section-divider">

        <h4 class="section-title">Información Opcional <span class="optional-indicator">(se puede completar después)</span></h4>

        <!-- Entry Price -->
        <div class="form-group-gcn optional-field">
            <label for="wp-entry-price">Precio de Entrada</label>
            <input 
                type="text" 
                id="wp-entry-price" 
                class="form-control-gcn" 
                :class="{ 'error': shouldShowError('entryPrice') }"
                :value="formatDisplayValue(formData.entry_price)"
                @input="event => handleNumericInput(event, 'entry_price')"
                @blur="event => { markFieldTouched('entryPrice'); formatNumericOnBlur(event, 'entry_price'); }"
                @focus="event => { markFieldTouched('entryPrice'); handleFocus(event, 'entry_price'); }"
                placeholder="0.00"
                :disabled="isSubmitting">
            <small class="text-secondary">Precio al que planeas entrar (opcional)</small>
            <small v-if="shouldShowError('entryPrice')" class="error-message-gcn">{{ entryPriceError }}</small>
        </div>

        <!-- Support Price -->
        <div class="form-group-gcn optional-field">
            <label for="wp-support-price">Precio de Soporte</label>
            <input 
                type="text" 
                id="wp-support-price" 
                class="form-control-gcn" 
                :class="{ 'error': shouldShowError('supportPrice') }"
                :value="formatDisplayValue(formData.support_price)"
                @input="event => handleNumericInput(event, 'support_price')"
                @blur="event => { markFieldTouched('supportPrice'); formatNumericOnBlur(event, 'support_price'); }"
                @focus="event => { markFieldTouched('supportPrice'); handleFocus(event, 'support_price'); }"
                placeholder="0.00"
                :disabled="isSubmitting">
            <small class="text-secondary">Precio de soporte o stop loss (opcional)</small>
            <small v-if="shouldShowError('supportPrice')" class="error-message-gcn">{{ supportPriceError }}</small>
        </div>

        <!-- Support Date -->
        <div class="form-group-gcn optional-field">
            <label for="wp-support-date">Fecha de Soporte</label>
            <input 
                type="date" 
                id="wp-support-date" 
                class="form-control-gcn" 
                :class="{ 'error': shouldShowError('supportDate') }"
                :value="formattedSupportDateForInput" 
                @input="updateSupportDate" 
                @blur="markFieldTouched('supportDate'); validateSupportDate()"
                @focus="markFieldTouched('supportDate')"
                :disabled="isSubmitting">
            <small class="text-secondary">Fecha hasta la cual es válido el soporte (opcional) - Formato API: {{ formData.support_date || 'Sin fecha' }}</small>
            <small v-if="shouldShowError('supportDate')" class="error-message-gcn">{{ supportDateError }}</small>
        </div>

        <!-- CFD Margin (only for CFD instruments) -->
        <div class="form-group-gcn optional-field" v-if="formData.instrument_type === 'CFD'">
                              <label for="wp-cfd-margin">Margen CFD <span class="required">*</span></label>
            <input 
                type="text" 
                id="wp-cfd-margin" 
                class="form-control-gcn" 
                :class="{ 'error': shouldShowError('cfdMargin') }"
                :value="formatDisplayValue(formData.cfd_margin)"
                @input="event => handleNumericInput(event, 'cfd_margin')"
                @blur="event => { markFieldTouched('cfdMargin'); formatNumericOnBlur(event, 'cfd_margin'); }"
                @focus="event => { markFieldTouched('cfdMargin'); handleFocus(event, 'cfd_margin'); }"
                :required="formData.instrument_type === 'CFD'"
                :disabled="isSubmitting"
                placeholder="0.00">
            <small class="text-secondary">Margen requerido para CFDs (requerido para CFDs)</small>
            <small v-if="shouldShowError('cfdMargin')" class="error-message-gcn">{{ cfdMarginError }}</small>
        </div>

        <!-- Instrument Type -->
        <div class="form-group-gcn optional-field">
            <label for="wp-instrument-type">Tipo de Instrumento</label>
            <select id="wp-instrument-type" class="form-control-gcn" v-model="formData.instrument_type" @change="handleInstrumentTypeChange" :disabled="isSubmitting">
                <option value="STOCK">Acción (Stock)</option>
                <option value="CFD">CFD</option>
            </select>
            <small class="text-secondary">Tipo de instrumento financiero (por defecto: Acción)</small>
        </div>

        <!-- Form Status Indicator -->
        <div v-if="isFormValid && submitAttempted" class="form-status success">
            <span class="status-icon">✓</span> Formulario completado correctamente
        </div>
        <div v-else-if="hasErrors && submitAttempted" class="form-status error">
            <span class="status-icon">⚠</span> Por favor, corrija los errores antes de continuar
        </div>

        <!-- Hidden submit button -->
        <button type="submit" style="display: none;"></button>
    </form>
</template>

<script>
import apiService, { formatDateDMY, parseDate } from './apiService';
import _ from 'lodash';

const ENTRY_TYPES = { 0: 'RR', 1: 'RRA', 2: 'LT', 3: 'LTA', 6: 'Tipo 6?', 7: 'Tipo 7?' };

export default {
    name: 'WaitingPositionForm',
    props: {
        portfolioId: { type: String, required: true },
        error: { type: String, default: null },
        isSubmitting: { type: Boolean, default: false }
    },
    data() {
        return {
            loadingTickerData: false,
            tickerSearch: '',
            formData: {
                ticker: '',
                entry_type: '',
                direction: '1',
                entry_price: null,
                support_price: null,
                support_date: '',
                instrument_type: 'STOCK',
                cfd_margin: null
            },
            tickerSuggestions: [],
            showSuggestions: false,
            tickerInfo: { entries: null, currency: null },
            debounceFetchAutocomplete: null,
            // Validation errors
            tickerError: null,
            entryTypeError: null,
            entryPriceError: null,
            supportPriceError: null,
            supportDateError: null,
            cfdMarginError: null,
            // Validation states
            tickerValid: false,
            entryTypeValid: false,
            entryPriceValid: true, // Optional field starts as valid
            supportPriceValid: true, // Optional field starts as valid
            supportDateValid: true, // Optional field starts as valid
            cfdMarginValid: true, // Will be validated when needed
            // Touch states
            touchedFields: {
                ticker: false,
                entryType: false,
                entryPrice: false,
                supportPrice: false,
                supportDate: false,
                cfdMargin: false
            },
            submitAttempted: false
        };
    },
    computed: {
        availableEntryTypes() {
            if (!this.tickerInfo.entries) return [];
            return this.tickerInfo.entries.map(entry => ({
                value: entry.tipus_entrada,
                name: ENTRY_TYPES[entry.tipus_entrada] || `Tipo ${entry.tipus_entrada}`
            }));
        },
        formattedSupportDateForInput() { 
            return this.formatDateForInput(this.formData.support_date); 
        },
        isFormValid() {
            // Required fields validation
            let requiredValid = this.tickerValid && this.entryTypeValid;
            
            // CFD margin is required if instrument type is CFD
            if (this.formData.instrument_type === 'CFD') {
                requiredValid = requiredValid && this.cfdMarginValid;
            }

            // Optional fields must be valid if they have values
            const optionalValid = this.entryPriceValid && this.supportPriceValid && this.supportDateValid;
            
            return requiredValid && optionalValid && !this.hasErrors;
        },
        hasErrors() {
            return !!(this.tickerError || this.entryTypeError || this.entryPriceError || 
                     this.supportPriceError || this.supportDateError || this.cfdMarginError);
        }
    },
    created() {
        this.debounceFetchAutocomplete = _.debounce(this.fetchAutocomplete, 300);
        this.validateInitialValues();
    },
    methods: {
        markFieldTouched(fieldName) {
            this.touchedFields[fieldName] = true;
        },

        shouldShowError(fieldName) {
            return (this.touchedFields[fieldName] || this.submitAttempted) && this[fieldName + 'Error'];
        },

        validateInitialValues() {
            this.validateTicker();
            this.validateEntryType();
            this.validateOptionalFields();
        },

        validateOptionalFields() {
            this.validateEntryPrice();
            this.validateSupportPrice();
            this.validateSupportDate();
            this.validateCfdMargin();
        },

        formatDateForInput(dmyDate) {
            if (!dmyDate) return '';
            const date = parseDate(dmyDate);
            if (!date) return '';
            return date.toISOString().split('T')[0];
        },

        updateSupportDate(event) {
            const dateValue = event.target.value;
            this.formData.support_date = dateValue ? formatDateDMY(new Date(dateValue + 'T00:00:00')) : '';
            this.validateSupportDate();
        },

        validateTicker() {
            if (!this.formData.ticker || !this.tickerSearch) {
                this.tickerError = 'Debe seleccionar un ticker válido.';
                this.tickerValid = false;
            } else {
                this.tickerError = null;
                this.tickerValid = true;
            }
        },

        validateEntryType() {
            if (this.formData.entry_type === '' || this.formData.entry_type === null || this.formData.entry_type === undefined) {
                this.entryTypeError = 'Debe seleccionar un tipo de entrada.';
                this.entryTypeValid = false;
            } else {
                this.entryTypeError = null;
                this.entryTypeValid = true;
            }
        },

        validateOptionalNumericField(value, fieldName) {
            // For optional fields, empty/null is allowed
            if (value === null || value === undefined || value === '') {
                return null;
            }
            
            const numericValue = parseFloat(value);
            if (isNaN(numericValue)) {
                return `${fieldName} debe ser un número válido.`;
            }
            
            if (numericValue < 0) {
                return `${fieldName} no puede ser negativo.`;
            }
            
            return null;
        },

        validateRequiredNumericField(value, fieldName) {
            if (value === null || value === undefined || value === '') {
                return `${fieldName} es requerido.`;
            }
            
            const numericValue = parseFloat(value);
            if (isNaN(numericValue)) {
                return `${fieldName} debe ser un número válido.`;
            }
            
            if (numericValue < 0) {
                return `${fieldName} no puede ser negativo.`;
            }
            
            return null;
        },

        validateEntryPrice() {
            const error = this.validateOptionalNumericField(this.formData.entry_price, 'Precio de entrada');
            this.entryPriceError = error;
            this.entryPriceValid = !error;
        },

        validateSupportPrice() {
            const error = this.validateOptionalNumericField(this.formData.support_price, 'Precio de soporte');
            this.supportPriceError = error;
            this.supportPriceValid = !error;
        },

        validateSupportDate() {
            // Optional field validation
            if (!this.formData.support_date) {
                this.supportDateError = null;
                this.supportDateValid = true;
                return;
            }
            
            const parsedDate = parseDate(this.formData.support_date);
            if (!parsedDate) {
                this.supportDateError = 'La fecha de soporte no es válida.';
                this.supportDateValid = false;
                return;
            }
            
            const today = new Date();
            today.setHours(23, 59, 59, 999);
            if (parsedDate < today) {
                this.supportDateError = 'La fecha de soporte no puede ser en el pasado.';
                this.supportDateValid = false;
                return;
            }
            
            this.supportDateError = null;
            this.supportDateValid = true;
        },

        validateCfdMargin() {
            if (this.formData.instrument_type === 'CFD') {
                const error = this.validateRequiredNumericField(this.formData.cfd_margin, 'Margen CFD');
                this.cfdMarginError = error;
                this.cfdMarginValid = !error;
            } else {
                this.cfdMarginError = null;
                this.cfdMarginValid = true;
            }
        },

        formatDisplayValue(value) {
            if (value === null || value === undefined) return '';
            if (typeof value === 'number') {
                return value.toFixed(2);
            }
            return value;
        },

        handleFocus(event, modelKey) {
            if (this.formData[modelKey] !== null && this.formData[modelKey] !== undefined) {
                event.target.value = this.formData[modelKey].toString();
            }
        },

        handleNumericInput(event, modelKey) {
            let value = event.target.value;
            // Only allow digits, dots, and commas (no dashes/minus signs)
            let sanitizedValue = value.replace(/[^\d.,]/g, '');
            
            // Handle decimal separators: find the last comma or dot as decimal separator
            const lastCommaIndex = sanitizedValue.lastIndexOf(',');
            const lastDotIndex = sanitizedValue.lastIndexOf('.');
            const lastDecimalIndex = Math.max(lastCommaIndex, lastDotIndex);
            
            if (lastDecimalIndex !== -1) {
                // Split into integer and decimal parts
                const integerPart = sanitizedValue.substring(0, lastDecimalIndex).replace(/[.,]/g, ''); // Remove all dots/commas from integer part
                const decimalPart = sanitizedValue.substring(lastDecimalIndex + 1);
                
                // Limit decimal part to 2 digits
                const limitedDecimalPart = decimalPart.substring(0, 2);
                
                // Reconstruct with dot as decimal separator
                // Always include the dot if there was a decimal separator, even if decimal part is empty
                sanitizedValue = integerPart + '.' + limitedDecimalPart;
            }
            
            // Prevent starting with decimal point - require leading zero
            if (sanitizedValue.startsWith('.')) {
                sanitizedValue = '0' + sanitizedValue;
            }

            event.target.value = sanitizedValue;

            if (sanitizedValue === '') {
                this.formData[modelKey] = null;
            } else if (!isNaN(parseFloat(sanitizedValue)) && isFinite(sanitizedValue)) {
                this.formData[modelKey] = sanitizedValue;
            } else if (sanitizedValue.endsWith('.')) {
                this.formData[modelKey] = sanitizedValue;
            } else {
                this.formData[modelKey] = null;
            }

            // Real-time validation
            switch(modelKey) {
                case 'entry_price':
                    this.validateEntryPrice();
                    break;
                case 'support_price':
                    this.validateSupportPrice();
                    break;
                case 'cfd_margin':
                    this.validateCfdMargin();
                    break;
            }
        },

        formatNumericOnBlur(event, modelKey) {
            let value = this.formData[modelKey];
            
            if (value === null || String(value).trim() === '') {
                this.formData[modelKey] = null;
                return;
            }
            
            if(typeof value === 'string' && value.endsWith('.')) {
                value = value.slice(0, -1);
            }

            const numericValue = parseFloat(value);
            if (!isNaN(numericValue) && isFinite(numericValue)) {
                // Round to 2 decimal places
                const roundedValue = Math.round(numericValue * 100) / 100;
                this.formData[modelKey] = roundedValue;
            } else {
                this.formData[modelKey] = null;
            }

            // Validate after formatting
            switch(modelKey) {
                case 'entry_price':
                    this.validateEntryPrice();
                    break;
                case 'support_price':
                    this.validateSupportPrice();
                    break;
                case 'cfd_margin':
                    this.validateCfdMargin();
                    break;
            }
        },

        async fetchAutocomplete() {
            if (this.tickerSearch.length < 2) { 
                this.tickerSuggestions = []; 
                this.showSuggestions = false; 
                return; 
            }
            try {
                const results = await apiService.getTickerAutocomplete(this.tickerSearch);
                this.tickerSuggestions = Object.entries(results).map(([ticker, valueString]) => {
                    const parts = valueString.split(',');
                    return { ticker: ticker, name: parts[1] || '' };
                });
                this.showSuggestions = true;
            } catch (err) { 
                console.error("Autocomplete error:", err); 
                this.tickerSuggestions = []; 
                this.showSuggestions = false; 
            }
        },

        selectTicker(ticker) {
            this.tickerSearch = ticker;
            this.formData.ticker = ticker;
            this.showSuggestions = false;
            this.tickerSuggestions = [];
            this.validateTicker();
            this.fetchTickerInfo();
        },

        async fetchTickerInfo() {
            if (!this.formData.ticker || !this.formData.direction) return;
            this.loadingTickerData = true;
            this.tickerInfo = { entries: null, currency: null };
            this.formData.entry_type = '';
            try {
                const data = await apiService.getTickerData(this.formData.ticker, this.formData.direction);
                if (data && data.currency !== false) {
                    this.tickerInfo = data;
                    this.validateEntryType();
                } else {
                    this.$emit('error', `No se encontró información para ${this.formData.ticker}.`);
                }
            } catch (err) { 
                this.$emit('error', `Error buscando datos para ${this.formData.ticker}: ${err.message}`); 
            } finally { 
                this.loadingTickerData = false; 
            }
        },

        handleTickerInfoChange() { 
            if (this.formData.ticker) { 
                this.fetchTickerInfo(); 
            } 
        },

        handleInstrumentTypeChange() {
            // Reset CFD margin when switching from CFD to STOCK
            if (this.formData.instrument_type === 'STOCK') {
                this.formData.cfd_margin = null;
            }
            this.validateCfdMargin();
        },

        submitForm() {
            this.submitAttempted = true;
            
            // Mark all relevant fields as touched
            this.markFieldTouched('ticker');
            this.markFieldTouched('entryType');
            
            // Mark optional fields as touched if they have values
            if (this.formData.entry_price !== null) this.markFieldTouched('entryPrice');
            if (this.formData.support_price !== null) this.markFieldTouched('supportPrice');
            if (this.formData.support_date) this.markFieldTouched('supportDate');
            if (this.formData.instrument_type === 'CFD') this.markFieldTouched('cfdMargin');

            // Validate all fields
            this.validateTicker();
            this.validateEntryType();
            this.validateOptionalFields();

            if (!this.isFormValid || this.hasErrors) {
                this.$emit('error', 'Por favor, corrija los errores en el formulario antes de continuar.');
                return;
            }

            // Prepare data for submission
            let dataToSend = {
                ticker: this.formData.ticker,
                entry_type: this.formData.entry_type,
                direction: this.formData.direction,
                instrument_type: this.formData.instrument_type
            };

            // Add optional fields only if they have values
            if (this.formData.entry_price !== null) {
                dataToSend.entry_price = parseFloat(this.formData.entry_price);
            }
            if (this.formData.support_price !== null) {
                dataToSend.support_price = parseFloat(this.formData.support_price);
            }
            if (this.formData.support_date) {
                dataToSend.support_date = this.formData.support_date;
            }
            if (this.formData.instrument_type === 'CFD' && this.formData.cfd_margin !== null) {
                dataToSend.cfd_margin = parseFloat(this.formData.cfd_margin);
            }

            this.$emit('submit', dataToSend);
        }
    },
    watch: {
        tickerSearch(newVal) {
            if (!newVal) {
                this.formData.ticker = '';
                this.tickerInfo = { entries: null, currency: null };
                this.formData.entry_type = '';
                this.tickerValid = false;
                this.entryTypeValid = false;
                this.touchedFields.ticker = false;
                this.touchedFields.entryType = false;
                this.validateTicker();
                this.validateEntryType();
            }
        }
    }
}
</script>

<style scoped>
.autocomplete-suggestions {
    position: relative;
    background-color: var(--color-background);
    border: 1px solid var(--color-outline);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
    list-style: none;
    margin: 0;
    padding: 0;
    max-height: 150px;
    overflow-y: auto;
    z-index: 1051;
    width: 100%;
    color: var(--color-text-primary);
}

.autocomplete-suggestions li { 
    padding: 8px 12px; 
    cursor: pointer; 
}

.autocomplete-suggestions li:hover { 
    background-color: var(--color-active-surface); 
}

.loading-indicator-gcn.small { 
    font-size: 0.9em; 
    text-align: left; 
    padding: 5px 0; 
}

/* Section styling */
.section-title {
    color: var(--color-text-primary);
    font-size: 1.1em;
    margin-bottom: 15px;
    border-bottom: 2px solid var(--color-primary);
    padding-bottom: 5px;
}

.section-divider {
    margin: 30px 0;
    border-color: #eee;
}

.required-indicator {
    font-size: 0.9em;
    color: var(--color-error);
    font-weight: normal;
}

.optional-indicator {
    font-size: 0.9em;
    color: var(--color-text-secondary);
    font-weight: normal;
}

.optional-field {
    background-color: rgba(0, 123, 255, 0.05);
    border-left: 3px solid rgba(0, 123, 255, 0.3);
    padding-left: 15px;
    margin-bottom: 20px;
}

/* Enhanced validation styles */
.required {
    color: var(--color-error);
    font-weight: bold;
}

.error-message-gcn {
    color: var(--color-error);
    font-size: 0.8em;
    display: block;
    margin-top: 4px;
}

.text-secondary {
    color: var(--color-text-secondary);
}

.radio-label {
    margin-right: 15px; 
    font-weight: normal;
}

.form-control-gcn.error {
    border-color: var(--color-error);
    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
}

.form-status {
    padding: 10px;
    border-radius: 4px;
    margin-top: 15px;
    font-size: 0.9em;
    display: flex;
    align-items: center;
    gap: 8px;
}

.form-status.success {
    background-color: #d4edda;
    border: 1px solid #c3e6cb;
    color: #155724;
}

.form-status.error {
    background-color: #f8d7da;
    border: 1px solid #f5c6cb;
    color: #721c24;
}

.status-icon {
    font-weight: bold;
    font-size: 1.1em;
}
</style>
